// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "@tanstack/react-router";
import { Loader2, Pencil } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";
import { ApiError } from "@/api/api-error";
import { useCreateFirmwareMutation } from "@/api/hooks/use-firmware";
import { MemoryStickPlusIcon } from "@/components/icons";
import { FileDropZone } from "@/components/shared/file-drop-zone";
import { WizardPane } from "@/components/shared/wizard-pane";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";

const DEVICE_TYPES = ["esp32", "stridingcam", "sc25"] as const;
const CPU_TYPES = ["esp32", "t41"] as const;
// Accepted upload per device type — a single binary for esp32/stridingcam, a zip archive for sc25.
const ACCEPT: Record<(typeof DEVICE_TYPES)[number], string> = {
  esp32: ".bin",
  sc25: ".zip",
  stridingcam: ".txz",
};

// Derive the sortable numeric build code from a version string (ported from the backend's
// legacy OTA logic). e.g. 0.10.3 → 100300, 0.10.3-dev → 100201, 0.10.0-rc2 → 99902.
function firmwareVersionCode(version: string): number {
  const segments = (version.match(/\d+/g) ?? []).map(Number);
  const a = segments[0] ?? 0;
  let b = segments[1] ?? 0;
  let c = segments[2] ?? 0;
  let d = segments[3] ?? 0;
  if (version.includes("-")) {
    if (!d) d = 1;
    if (c) c -= 1;
    else if (b) {
      c = 99;
      b -= 1;
    }
  }
  return Number([a, b, c, d].map((v) => String(v).padStart(2, "0")).join(""));
}

// Require at least MAJOR.MINOR; PATCH and any `-`/`.` suffix are optional (e.g. 1.0, 0.10.3,
// 0.0.2-001-debug-t41). This blocks non-numeric strings like "hotfix" that derive a build of 0.
const VERSION_FORMAT = /^\d+\.\d+(\.\d+)?([-.].*)?$/;

const schema = z
  .object({
    deviceType: z.enum(DEVICE_TYPES),
    versionStr: z.string().min(1, "Required").regex(VERSION_FORMAT, "Use at least MAJOR.MINOR (e.g. 1.0)"),
    // Derived from versionStr (or set manually); the list sorts by this, so a 0/garbage build mis-orders.
    versionNum: z.number().int().min(1, "Version must be greater than 0"),
    cpuType: z.string(),
    changeLog: z.string(),
    isProduction: z.boolean(),
  })
  // CPU type is required for SC25 firmware.
  .superRefine((v, ctx) => {
    if (v.deviceType === "sc25" && !v.cpuType)
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Required", path: ["cpuType"] });
  });

type CreateFirmwareValues = z.infer<typeof schema>;

/** The two fields a duplicate can collide on — the backend checks them separately. */
type DuplicateField = "versionStr" | "versionNum";

/**
 * Where a failed create should be reported. A firmware that already exists for the device type is
 * the expected, actionable failure: the backend raises EntityAlreadyExistsError → HTTP 409 with a
 * detail naming the column that clashed (`otaVersion.versionStr:…` or `otaVersion.versionNum:…`),
 * and it checks the version string first, then the build number. Anything else goes to the dialog
 * banner, preferring the backend's own wording over a generic line.
 */
export type FirmwareCreateFailure =
  | { kind: "duplicate"; field: DuplicateField }
  | { kind: "message"; text: string }
  | { kind: "generic" };

export function classifyFirmwareCreateFailure(err: unknown): FirmwareCreateFailure {
  if (!(err instanceof ApiError)) return { kind: "generic" };
  if (err.status === 409) {
    return { kind: "duplicate", field: /versionnum/i.test(err.detail ?? "") ? "versionNum" : "versionStr" };
  }
  return err.message ? { kind: "message", text: err.message } : { kind: "generic" };
}

const DEFAULTS: CreateFirmwareValues = {
  deviceType: "esp32",
  versionStr: "",
  versionNum: 0,
  cpuType: "",
  changeLog: "",
  isProduction: false,
};

interface CreateFirmwareDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateFirmwareDialog({ open, onOpenChange }: CreateFirmwareDialogProps) {
  const { t } = useTranslation("back-office");
  const navigate = useNavigate();
  const createFirmware = useCreateFirmwareMutation();
  const [file, setFile] = useState<File | null>(null);
  const [buildManual, setBuildManual] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  /** The rejected pair, kept so the message can retract itself once either value is edited. */
  const [duplicate, setDuplicate] = useState<{ field: DuplicateField; versionStr: string; versionNum: number } | null>(
    null,
  );

  // Default (onSubmit) mode: nothing turns red while the user is still typing.
  const form = useForm<CreateFirmwareValues>({
    resolver: zodResolver(schema),
    defaultValues: DEFAULTS,
  });

  useEffect(
    function resetFormOnOpen() {
      if (open) {
        form.reset(DEFAULTS);
        setFile(null);
        setBuildManual(false);
        setFormError(null);
        setDuplicate(null);
      }
    },
    [open, form],
  );

  const deviceType = form.watch("deviceType");
  const accept = ACCEPT[deviceType];

  // Auto-derive the build number from the version string until the user edits it manually.
  const versionStr = form.watch("versionStr");
  useEffect(
    function deriveBuildFromVersion() {
      if (!buildManual) form.setValue("versionNum", firmwareVersionCode(versionStr.trim()));
    },
    [versionStr, buildManual, form],
  );

  // Whether Create is offered at all. Checked against the schema directly rather than through
  // `formState.isValid`, which only tracks live under a non-submit validation mode — this keeps
  // the button honest without making the fields flash red as the user types. The binary lives
  // outside RHF, so it is required here rather than in the schema.
  const values = form.watch();
  const canSubmit = schema.safeParse(values).success && file !== null;

  // A duplicate is only ever true of the exact pair the backend rejected, so it is derived rather
  // than cleared: edit either field and it stops applying. (The red field highlight retracts on its
  // own — a duplicate can only follow a submit, and from then on RHF re-validates on change.)
  const duplicateField =
    duplicate && duplicate.versionStr === values.versionStr && duplicate.versionNum === values.versionNum
      ? duplicate.field
      : null;

  async function onSubmit(values: CreateFirmwareValues) {
    // Unreachable via the button, which requires a file — this narrows the type for the upload.
    if (!file) return;
    setFormError(null);
    setDuplicate(null);
    try {
      const res = await createFirmware.mutateAsync({
        deviceType: values.deviceType,
        versionStr: values.versionStr.trim(),
        versionNum: values.versionNum,
        cpuType: (values.cpuType || undefined) as (typeof CPU_TYPES)[number] | undefined,
        changeLog: values.changeLog.trim() || undefined,
        isProduction: values.isProduction,
        file,
      });
      toast.success(t("firmware.dialog.created", { name: values.versionStr }));
      onOpenChange(false);
      navigate({ to: "/hardware/firmware/$firmwareId", params: { firmwareId: String(res.firmwareId) } });
    } catch (err) {
      // The global error toast is opt-in (debug preference) and only covers 422/5xx, so the dialog
      // has to report failures itself, or the submit looks like it did nothing.
      const failure = classifyFirmwareCreateFailure(err);
      if (failure.kind === "duplicate") {
        // Mark the clashing field so it turns red, but with no per-field text: the explanation is
        // rendered once across the whole row, where it has the width to read on one line.
        setDuplicate({ field: failure.field, versionStr: values.versionStr, versionNum: values.versionNum });
        form.setError(failure.field, { message: "" });
      } else {
        setFormError(failure.kind === "message" ? failure.text : t("firmware.dialog.createFailed"));
      }
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        id="dialog-create-ota-version"
        className="sm:max-w-xl p-0 gap-0 overflow-hidden flex flex-col"
        aria-describedby={undefined}
      >
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col">
            <div className="flex flex-row">
              <WizardPane icon={MemoryStickPlusIcon} />
              <div className="flex-1 flex flex-col min-w-0 min-h-0">
                <DialogHeader className="p-6 pb-0">
                  <DialogTitle>{t("firmware.dialog.createTitle")}</DialogTitle>
                </DialogHeader>

                <div className="flex flex-col gap-4 overflow-y-auto max-h-[60vh] p-6 pt-4">
                  <div className="flex flex-wrap gap-4">
                    <FormField
                      control={form.control}
                      name="deviceType"
                      render={({ field }) => (
                        <FormItem className="flex-1 min-w-40">
                          <FormLabel>{t("firmware.fields.deviceType")}</FormLabel>
                          <Select
                            value={field.value}
                            onValueChange={(v) => {
                              field.onChange(v);
                              // Clear a file whose extension no longer matches the new device type,
                              // and reset the CPU type (only relevant for sc25).
                              setFile(null);
                              form.setValue("cpuType", "");
                            }}
                          >
                            <FormControl>
                              <SelectTrigger className="w-full">
                                <SelectValue />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {DEVICE_TYPES.map((d) => (
                                <SelectItem key={d} value={d} className="font-mono">
                                  {d}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    {/* CPU type is only relevant for SC25 firmware. */}
                    {deviceType === "sc25" && (
                      <FormField
                        control={form.control}
                        name="cpuType"
                        render={({ field }) => (
                          <FormItem className="flex-1 min-w-40">
                            <FormLabel>{t("firmware.fields.cpuType")}</FormLabel>
                            <Select value={field.value} onValueChange={field.onChange}>
                              <FormControl>
                                <SelectTrigger className="w-full">
                                  <SelectValue placeholder={t("firmware.dialog.cpuPlaceholder")} />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {CPU_TYPES.map((c) => (
                                  <SelectItem key={c} value={c} className="font-mono">
                                    {c}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    )}
                  </div>

                  {/* Version + build number share a duplicate message spanning both columns. */}
                  <div className="space-y-1.5">
                    <div className="flex flex-wrap items-start gap-4">
                      <FormField
                        control={form.control}
                        name="versionStr"
                        render={({ field }) => (
                          <FormItem className="flex-1 min-w-40">
                            <FormLabel>{t("firmware.fields.versionStr")}</FormLabel>
                            <FormControl>
                              <Input {...field} className="font-mono" autoComplete="off" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="versionNum"
                        render={({ field }) => (
                          <FormItem className="flex-1 min-w-40">
                            <FormLabel>{t("firmware.fields.versionNum")}</FormLabel>
                            {/* FormControl wraps the Input, not the positioning div, so the label's
                              `for` and `aria-invalid` land on the field itself. */}
                            <div className="relative">
                              <FormControl>
                                <Input
                                  type="number"
                                  className={`font-mono tabular-nums ${buildManual ? "" : "border-dashed pr-14"}`}
                                  disabled={!buildManual}
                                  value={field.value}
                                  onChange={(e) => field.onChange(e.target.valueAsNumber || 0)}
                                />
                              </FormControl>
                              {!buildManual && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="absolute right-1 top-1/2 -translate-y-1/2 h-7 px-2 text-xs text-muted-foreground"
                                  onClick={() => setBuildManual(true)}
                                >
                                  <Pencil className="h-3 w-3 mr-1" />
                                  {t("buttons.edit")}
                                </Button>
                              )}
                            </div>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    {duplicateField && (
                      <p className="text-sm text-destructive">
                        {t(
                          duplicateField === "versionNum"
                            ? "firmware.dialog.buildNumberExists"
                            : "firmware.dialog.versionExists",
                        )}
                      </p>
                    )}
                  </div>

                  {/* Firmware binary — managed outside RHF; the backend computes md5 and the storage path. */}
                  <div className="space-y-1.5">
                    <Label>{t("firmware.fields.file")}</Label>
                    <FileDropZone
                      accept={accept}
                      selected={file ? { name: file.name, size: file.size } : undefined}
                      title={t("firmware.dialog.fileDropTitle")}
                      subtitle={t(
                        deviceType === "sc25" ? "firmware.dialog.fileHintArchive" : "firmware.dialog.fileHint",
                        {
                          ext: accept,
                        },
                      )}
                      invalid={!file && form.formState.isSubmitted}
                      onSelect={setFile}
                    />
                    {!file && form.formState.isSubmitted && (
                      <p className="text-sm text-destructive">{t("firmware.dialog.fileRequired")}</p>
                    )}
                  </div>

                  <FormField
                    control={form.control}
                    name="changeLog"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("firmware.fields.changeLog")}</FormLabel>
                        <FormControl>
                          <Textarea {...field} rows={2} className="min-h-14" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="isProduction"
                    render={({ field }) => (
                      <FormItem className="flex items-center gap-3">
                        <FormControl>
                          <Switch id="ota-production" checked={field.value} onCheckedChange={field.onChange} />
                        </FormControl>
                        <Label htmlFor="ota-production">{t("firmware.dialog.productionLabel")}</Label>
                      </FormItem>
                    )}
                  />

                  {formError && <p className="text-sm text-destructive">{formError}</p>}
                </div>
              </div>
            </div>

            <DialogFooter className="mx-0 mb-0 rounded-none rounded-b-lg">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  form.reset(DEFAULTS);
                  setFile(null);
                  onOpenChange(false);
                }}
              >
                {t("buttons.cancel")}
              </Button>
              {/* Gated on the schema and on having a binary to upload. */}
              <Button type="submit" disabled={!canSubmit || createFirmware.isPending}>
                {createFirmware.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <MemoryStickPlusIcon className="h-4 w-4" />
                )}
                {t("buttons.create")}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
